/**
 * 认证 Context
 * 管理用户登录状态、Token 和组织信息
 */
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import * as authApi from '@/api/auth';
import { tokenManager } from '@/api/client';

// 用户信息
interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'creator' | 'user';
  groups: string[];
  isActive: boolean;
}

// 组织信息
interface OrgInfo {
  id: string;
  name: string;
  logo: string;
  banner: string;
  primaryColor: string;
  secondaryColor: string;
  welcomeMessage: string;
}

// 登录响应（用于返回登录结果）
interface LoginResult {
  user: AuthUser;
  org: OrgInfo;
}

// Auth Context 类型
interface AuthContextType {
  user: AuthUser | null;
  org: OrgInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [org, setOrg] = useState<OrgInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // 登录
  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    const response = await authApi.login({ email, password });
    setUser(response.user);
    setOrg(response.org);
    return { user: response.user, org: response.org };
  }, []);

  // 登出
  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      setOrg(null);
      tokenManager.remove();
    }
  }, []);

  // 刷新用户信息
  const refreshUser = useCallback(async () => {
    if (!tokenManager.isValid()) {
      setUser(null);
      setOrg(null);
      return;
    }

    try {
      const response = await authApi.getCurrentUser();
      setUser(response.user);
      setOrg(response.org);
    } catch {
      setUser(null);
      setOrg(null);
      tokenManager.remove();
    }
  }, []);

  // 初始化时检查登录状态
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      if (tokenManager.isValid()) {
        try {
          const response = await authApi.getCurrentUser();
          setUser(response.user);
          setOrg(response.org);
        } catch {
          tokenManager.remove();
        }
      }
      setIsLoading(false);
    };

    initAuth();

    // 监听登出事件
    const handleLogout = () => {
      setUser(null);
      setOrg(null);
    };
    window.addEventListener('auth:logout', handleLogout);

    return () => {
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, []);

  const value: AuthContextType = {
    user,
    org,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
